<template>
  <section v-if="!loading" v-loading="saving">
    <el-header>
      <el-col :span="16">
        <el-page-header @back="cancel" :content="isNew ? 'Novo produto' : `${product.name}`"></el-page-header>
      </el-col>

      <el-col :span="8" style="text-align: right">
        <el-tooltip v-if="!isNew" :content="blingStatus.detail" placement="bottom">
          <el-tag :type="blingStatus.type" class="bling-status">{{ blingStatus.label }}</el-tag>
        </el-tooltip>
        <el-button type="primary" @click="save" :disabled="!canSave">{{ 'Salvar' }}</el-button>
      </el-col>
    </el-header>

    <el-form ref="form" :model="product" :rules="rules">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="Informações básicas" name="general">
          <el-row :gutter="20">
            <el-col :span="12">
              <!-- ID -->
              <el-form-item label="ID" prop="id">
                <el-input v-model="product.id" disabled></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <!-- ID From Supplier -->
              <el-form-item label="ID no fornecedor" prop="idFromSupplier">
                <el-input v-model="product.idFromSupplier"></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <!-- Barcode -->
          <el-form-item label="Códigos de barra" prop="barcodes">
            <el-input @keyup.enter.native="addBarcode" v-model="barcodeInput">
              <el-button slot="append" icon="el-icon-plus" @click="addBarcode"></el-button>
            </el-input>

            <div class="barcodes-container">
              <div v-if="product.barcodes && product.barcodes.length > 0">
                <el-tag v-for="(barcode, index) in product.barcodes" class="spaced" type="primary" :key="barcode"
                  :disable-transitions="true">
                  {{ barcode }}
                  <i class="el-tag__close el-icon-close" @click.stop="removeBarcode(index)" />
                </el-tag>
              </div>
              <div v-else>
                <div class="no-barcodes">Nenhum código registrado</div>
              </div>
            </div>
          </el-form-item>

          <!-- Display Name -->
          <el-form-item label="Nome de Exibição" prop="displayName">
            <el-input v-model="product.displayName"></el-input>
          </el-form-item>

          <!-- Name -->
          <el-form-item label="Nome Fiscal" prop="name">
            <el-input v-model="product.name"></el-input>
          </el-form-item>

          <!-- Description -->
          <el-form-item label="Descrição" prop="description">
            <el-input type="textarea" v-model="product.description"></el-input>
          </el-form-item>

          <!-- Brand -->
          <el-form-item label="Marca" prop="brandId">
            <snowflake-remote-search-select v-model="product.brandId" placeholder="Marca"
              :search-function="productBrandService.search" :find-function="productBrandService.getProductBrandById"
              :value-function="(item) => item.id">
            </snowflake-remote-search-select>
          </el-form-item>

          <el-divider />

          <el-row :gutter="20">
            <el-col :span="12">
              <!-- Base Score -->
              <el-form-item label="Score Base" prop="baseScore">
                <el-input-number v-model="product.baseScore" controls-position="right" :precision="2" :step="0.1" :min="0"
                  class="stretched" style="text-align: left">
                </el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <!-- Cost -->
              <el-form-item label="Custo" prop="cost">
                <el-input-number v-model="product.cost" controls-position="right" :precision="2" :step="0.1" :min="0"
                  class="stretched" style="text-align: left">
                </el-input-number>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="12">
              <!-- Market Price -->
              <el-form-item label="Preço de mercado" prop="marketPrice">
                <el-input-number v-model="product.marketPrice" controls-position="right" :precision="2" :step="0.1" :min="0"
                  class="stretched" style="text-align: left">
                </el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <!-- Perceived Value -->
              <el-form-item label="Valor Percebido" prop="perceivedValue">
                <el-input-number v-model="product.perceivedValue" controls-position="right" :precision="2" :step="0.1"
                  :min="0" class="stretched" style="text-align: left">
                </el-input-number>
              </el-form-item>
            </el-col>
          </el-row>

          <el-divider />

          <el-row :gutter="20">
            <el-col :span="12">
              <!-- Excluded from Simulations -->
              <el-form-item label="Excluído das Simulações" prop="excludedFromSimulations" style="text-align: left;">
                <el-switch v-model="product.excludedFromSimulations"></el-switch>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <!-- Is Non Fiscal -->
              <el-form-item label="Não Fiscal" prop="isNonFiscal" style="text-align: left;">
                <el-switch v-model="product.isNonFiscal"></el-switch>
              </el-form-item>
            </el-col>
          </el-row>

          <el-divider />

          <!-- Traits -->
          <el-form-item label="Traits" prop="traits">
            <product-traits-form v-model="product.traits" @problems="setTraitProblems">
            </product-traits-form>

            <!-- Edits the same traits, to compare and as a fallback while the form is on trial -->
            <el-collapse class="old-traits">
              <el-collapse-item title="Campo antigo de traits" name="old-traits">
                <snowflake-tag-select v-model="product.traits" :search-function="listTraits"
                  :id-function="(item) => item" :label-function="(item) => item"
                  :value-function="(item) => item">
                </snowflake-tag-select>
              </el-collapse-item>
            </el-collapse>
          </el-form-item>

          <!-- Dependent products -->
          <el-form-item label="Deve ser enviado com" prop="dependsOnProducts">
            <snowflake-tag-select v-model="product.dependsOnProducts" remote :search-function="productService.search"
              :find-function="productService.getProductById" :id-function="(item) => item.id"
              :label-function="(item) => item.name" :value-function="(item) => item.id" placeholder="Adicione um produto"
              no-tag-text="Nenhum produto selecionado">
            </snowflake-tag-select>
          </el-form-item>
        </el-tab-pane>

        <el-tab-pane label="Fiscal" name="fiscal">
          <el-divider content-position="left">Classificação fiscal (usada nas notas de saída)</el-divider>

          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="NCM" prop="ncm">
                <el-input v-model="product.ncm" placeholder="0000.00.00" maxlength="10"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="CEST" prop="cest">
                <el-input v-model="product.cest" placeholder="00.000.00" maxlength="9"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="Origem" prop="origin">
                <el-select v-model="product.origin" clearable placeholder="Origem" class="stretched">
                  <el-option v-for="option in originOptions" :key="option.value" :value="option.value"
                    :label="`${option.value} - ${option.label}`">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item label="Entra sem crédito de impostos (amostra, brinde ou item próprio)"
            prop="entersWithoutTaxCredit" style="text-align: left;">
            <el-switch v-model="product.entersWithoutTaxCredit"></el-switch>
            <span class="field-hint">
              Se o produto não tiver compra nos últimos 24 meses, o estoque é valorizado com taxa de crédito de 0%,
              em vez de 12%.
            </span>
          </el-form-item>

          <template v-if="!isNew">
            <el-divider content-position="left">Códigos de fornecedor (definidos na conciliação das notas de entrada)</el-divider>

            <el-table ref="supplierCodesTable" :data="supplierCodes" v-loading="loadingSupplierCodes" size="mini"
              empty-text="Nenhum código de fornecedor aponta para este produto">
              <el-table-column label="Fornecedor" min-width="200" show-overflow-tooltip>
                <template slot-scope="scope">{{ scope.row.supplierName || scope.row.code.companyDocumentRoot }}</template>
              </el-table-column>
              <el-table-column label="Código" min-width="140" show-overflow-tooltip>
                <template slot-scope="scope">{{ scope.row.code.supplierCode }}</template>
              </el-table-column>
              <el-table-column label="Unidades por unidade da nota" width="210" align="right">
                <template slot-scope="scope">{{ Number(scope.row.code.unitsPerSupplierUnit) }}</template>
              </el-table-column>
              <el-table-column label="Origem" width="210">
                <template slot-scope="scope">{{ supplierCodeSourceLabels[scope.row.code.source] }}</template>
              </el-table-column>
              <el-table-column label="Atualizado em" width="140">
                <template slot-scope="scope">{{ formatDateTime(scope.row.code.updatedAt) }}</template>
              </el-table-column>
              <el-table-column v-if="canSave" width="70" align="center" fixed="right">
                <template slot-scope="scope">
                  <el-tooltip content="Esquecer este código" placement="top">
                    <el-button size="mini" type="danger" plain icon="el-icon-delete"
                      @click="removeSupplierCode(scope.row.code)"></el-button>
                  </el-tooltip>
                </template>
              </el-table-column>
            </el-table>
          </template>
        </el-tab-pane>
      </el-tabs>
    </el-form>
  </section>
</template>
<script>
import Storage from '@/storage';

import ProductBrandService from '@/services/stock/ProductBrandService';
import ProductService from '@/services/stock/ProductService';
import SupplierProductCodeService from '@/services/fiscal/SupplierProductCodeService';
import TraitService from '@/services/stock/TraitService';

import SnowflakeRemoteSearchSelect from '@/components/input/RemoteSearchSelect.vue';
import SnowflakeTagSelect from '@/components/input/TagSelect.vue';
import { formatDateTime, supplierCodeSourceLabels } from '@/views/fiscal/purchase-invoice/labels';
import ProductTraitsForm from './TraitsForm.vue';

const newProduct = () => ({
  barcodes: [],
  traits: [],
  excludedFromSimulations: false,
  isNonFiscal: false,
  entersWithoutTaxCredit: false,
});

export default {
  name: 'product-detail',
  components: {
    ProductTraitsForm,
    SnowflakeRemoteSearchSelect,
    SnowflakeTagSelect,
  },
  data() {
    return {
      rules: {
        externalId: [{ required: true, trigger: 'blur' }],
        name: [{ required: true, trigger: 'blur' }],
        displayName: [{ required: true, trigger: 'blur' }],
        brandId: [{ required: true, trigger: 'blur' }],
        baseScore: [{ required: true, trigger: 'blur' }],
        cost: [{ required: true, trigger: 'blur' }],
        marketPrice: [{ required: true, trigger: 'blur' }],
        perceivedValue: [{ required: true, trigger: 'blur' }],
        ncm: [{ pattern: /^(\d{4}\.?\d{2}\.?\d{2})?$/, message: 'O NCM deve ter 8 dígitos', trigger: 'blur' }],
        cest: [{ pattern: /^(\d{2}\.?\d{3}\.?\d{2})?$/, message: 'O CEST deve ter 7 dígitos', trigger: 'blur' }],
      },
      originOptions: [
        { value: '0', label: 'Nacional' },
        { value: '1', label: 'Estrangeira, importação direta' },
        { value: '2', label: 'Estrangeira, adquirida no mercado interno' },
        { value: '3', label: 'Nacional, conteúdo de importação acima de 40% e até 70%' },
        { value: '4', label: 'Nacional, produção conforme processos produtivos básicos' },
        { value: '5', label: 'Nacional, conteúdo de importação até 40%' },
        { value: '6', label: 'Estrangeira, importação direta sem similar nacional' },
        { value: '7', label: 'Estrangeira, adquirida no mercado interno sem similar nacional' },
        { value: '8', label: 'Nacional, conteúdo de importação acima de 70%' },
      ],
      activeTab: 'general',
      loading: true,
      saving: false,
      product: newProduct(),
      traitProblems: [],
      traitErrorsBlockSave: false,
      barcodeInput: null,
      supplierCodes: [],
      loadingSupplierCodes: false,
      supplierCodeSourceLabels,
      productBrandService: ProductBrandService,
      productService: ProductService,
    };
  },
  async created() {
    if (this.$route.name === 'product-new') {
      this.product = newProduct();
      this.loading = false;

      return;
    }

    const product = await ProductService.getProductById(this.$route.params.id);

    this.product = this.interfaceProduct(product);
    this.loading = false;

    this.loadSupplierCodes();
  },
  computed: {
    isNew() {
      return this.$route.name === 'product-new';
    },
    canSave() {
      const roles = this.authRoles();

      return roles.includes('admin') || roles.includes('owner') || roles.includes('financial') || roles.includes('operations-lead');
    },
    // Bling's registry keeps a copy of the product's name, cost and fiscal codes, sent whenever the product changes
    blingStatus() {
      const { blingId, blingSyncedAt, updatedAt } = this.product;
      const sent = Boolean(blingSyncedAt) && new Date(blingSyncedAt) >= new Date(updatedAt);

      if (!sent) {
        return {
          type: 'warning',
          label: 'Bling: pendente',
          detail: `As alterações de ${formatDateTime(updatedAt)} ainda não chegaram ao Bling. O envio é tentado de novo a cada hora.`,
        };
      }

      if (!blingId) {
        return {
          type: 'info',
          label: 'Bling: não vinculado',
          detail: 'O produto ainda não está no Bling. Ele será criado lá na próxima vez que for alterado.',
        };
      }

      return {
        type: 'success',
        label: 'Bling: em dia',
        detail: `No Bling com o ID ${blingId}, com as alterações de ${formatDateTime(blingSyncedAt)}.`,
      };
    },
  },
  watch: {
    activeTab(tab) {
      // A table laid out while its tab was hidden sizes its fixed action column wrong, cropping the buttons
      if (tab === 'fiscal' && this.$refs.supplierCodesTable) {
        this.$nextTick(() => this.$refs.supplierCodesTable.doLayout());
      }
    },
  },
  methods: {
    authRoles() {
      const encodedRole = Storage.get(Storage.keys.auth.role);
      const encodedRoles = Storage.get(Storage.keys.auth.roles);

      return encodedRoles ? JSON.parse(atob(encodedRoles)) : (encodedRole ? [atob(encodedRole)] : null);
    },
    async save() {
      const invalidFields = await this.$refs.form.validate().then(() => null, (fields) => fields);

      if (invalidFields) {
        // Shows the tab holding the errors, since one the open tab hides would block saving without a visible reason
        const fiscalFields = ['ncm', 'cest', 'origin'];
        const onlyFiscal = Object.keys(invalidFields).every((field) => fiscalFields.includes(field));
        this.activeTab = onlyFiscal ? 'fiscal' : 'general';

        return;
      }

      if (!(await this.traitsAllowSaving())) return;

      this.saving = true;

      try {
        if (this.$route.name === 'product-new') {
          const created = await this.create();
          this.$router.push({
            name: 'product-detail',
            params: { id: created.id },
          });

          // Read again, since creating it in Bling happens after the create response is built
          this.product = this.interfaceProduct(await ProductService.getProductById(created.id));
        } else {
          this.product = this.interfaceProduct(await this.update());
        }
      } catch (error) {
        this.$message.error(`Não foi possível salvar: ${error.message}`);

        return;
      } finally {
        this.saving = false;
      }

      this.$message.success('Salvo com sucesso.');
    },
    // The traits form already lists what's wrong. The API refuses the same errors once they block
    // saving; until then, saving them takes a confirmation
    async traitsAllowSaving() {
      if (!this.traitProblems.some(problem => problem.level === 'ERROR')) return true;

      this.activeTab = 'general';

      if (this.traitErrorsBlockSave) {
        this.$message.error('Corrija os traits antes de salvar.');

        return false;
      }

      try {
        await this.$confirm(
          'Os traits têm erros que o formulário novo vai bloquear quando sair do teste. '
          + 'Salvar mesmo assim?',
          'Traits com erros',
          { confirmButtonText: 'Salvar', cancelButtonText: 'Corrigir', type: 'warning' },
        );
      } catch (cancelled) {
        return false;
      }

      return true;
    },
    setTraitProblems(problems, errorsBlockSave) {
      this.traitProblems = problems;
      this.traitErrorsBlockSave = errorsBlockSave;
    },
    // Every trait the API accepts, for the old trait field
    listTraits() {
      return TraitService.schema().then(schema => ({
        results: [
          ...schema.categories.flatMap(category => [category.trait, ...category.productTypes]),
          ...schema.groups.flatMap(group => group.traits),
        ],
      }));
    },
    cancel() {
      this.$router.back();
    },
    create() {
      return ProductService.create(this.apiProduct());
    },
    update() {
      return ProductService.update(this.product.id, this.apiProduct());
    },
    apiProduct() {
      const product = Object.assign({}, this.product);

      product.name = product.name.trim();
      product.displayName = product.displayName.trim();

      product.baseScore = Math.round(product.baseScore * 100);
      product.cost = Math.round(product.cost * 100);
      product.perceivedValue = Math.round(product.perceivedValue * 100);
      product.marketPrice = Math.round(product.marketPrice * 100);

      // The API clears a fiscal code when it receives an empty value
      product.ncm = product.ncm || '';
      product.cest = product.cest || '';
      product.origin = product.origin || '';

      return product;
    },
    interfaceProduct(apiProduct) {
      const product = Object.assign({}, apiProduct);
      product.baseScore /= 100;
      product.cost /= 100;
      product.perceivedValue /= 100;
      product.marketPrice /= 100;

      return product;
    },
    formatDateTime,
    async loadSupplierCodes() {
      this.loadingSupplierCodes = true;

      try {
        this.supplierCodes = await SupplierProductCodeService.findByProduct(this.$route.params.id);
      } catch (error) {
        this.$message.error(`Não foi possível carregar os códigos de fornecedor: ${error.description || error.message}`);
      } finally {
        this.loadingSupplierCodes = false;
      }
    },
    async removeSupplierCode(code) {
      try {
        await this.$confirm(
          `Esquecer o código ${code.supplierCode} deste fornecedor? As próximas notas deixam de ser vinculadas por ele; `
          + 'os itens já vinculados continuam com este produto.',
        );
      } catch (cancelled) {
        return;
      }

      try {
        await SupplierProductCodeService.remove(code.companyDocumentRoot, code.supplierCode);
        this.$message.success(`Código ${code.supplierCode} esquecido.`);
        await this.loadSupplierCodes();
      } catch (error) {
        this.$message.error(error.description || error.message);
      }
    },
    removeBarcode(index) {
      this.product.barcodes.splice(index, 1);
    },
    addBarcode() {
      if (!this.product.barcodes) {
        this.product.barcodes = [];
      }

      if (this.product.barcodes.indexOf(this.barcodeInput) >= 0) {
        this.$message.warning('Código de barras já inserido.');
      } else {
        this.product.barcodes.push(this.barcodeInput);
      }

      this.barcodeInput = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.bling-status {
  margin-right: 12px;
  cursor: default;
}

.old-traits {
  margin-top: 12px;
}

span.field-hint {
  margin-left: 12px;
  font-size: 12px;
  color: #5e7382;
}

div.barcodes-container {
  text-align: left;
  border: #c1c1c1 dashed 1.5px;
  border-radius: 4px;
  margin-top: 8px;
  padding: 8px;

  div.no-barcodes {
    text-align: center;
    font-size: 12;
    line-height: 40px;
    color: #5e7382;
  }

  .el-tag {
    margin: 4px;
  }
}
</style>
